import { AxiosError } from "axios";

import { getAccessToken, getCustomerId } from "@/helpers/auth-helpers";
import { ICustomerPlatform, ICustomerPlatformInput } from "@/interfaces/customer-platform.interfaces";
import { apiT2B } from "@/lib/axios";
import { getGupshupWabaInfo } from "./get-waba-info";
import { getGupshupProfilePhoto } from "./get-profile-photo";

const baseCustomerPlatformUrl = "api/v1/customerplatform";
const mediaSaveFromUrl = "api/v1/midia/savefromurl";

export async function updateGupshupGeneralInfo(
  whatsPlatform: ICustomerPlatform
): Promise<ICustomerPlatform> {
  try {
    const userToken = getAccessToken();
    const customerId = getCustomerId();

    if (!userToken || !customerId) {
      throw new Error("Authentication required");
    }

    // Buscar o objeto completo da API para preservar todos os campos
    // Isso evita que campos como configuration, accessToken, etc. sejam perdidos
    let fullPlatform: ICustomerPlatform;
    try {
      const response = await apiT2B.get(`${baseCustomerPlatformUrl}/${whatsPlatform.id}`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          CustomerId: customerId,
        },
      });
      fullPlatform = response.data;
    } catch (error) {
      // Se não conseguir buscar, usar o objeto passado como fallback
      fullPlatform = whatsPlatform;
    }

    const appId = fullPlatform.customerExternalId;

    if (!appId) {
      throw new Error("Missing App ID");
    }

    let display_phone_number: string | undefined;
    let verified_name: string | undefined;
    let publicUrl: string | undefined;

    const needsPhoneNumber = !fullPlatform.displayNumber;
    const needsDisplayName = !fullPlatform.displayName;
    const needsAccountImage = !fullPlatform.accountImage;

    if (needsPhoneNumber || needsDisplayName) {
      try {
        const wabaInfo = await getGupshupWabaInfo(appId);

        if (needsPhoneNumber && wabaInfo.phone) {
          display_phone_number = wabaInfo.phone;
        }

        if (needsDisplayName && wabaInfo.verifiedName) {
          verified_name = wabaInfo.verifiedName;
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 400) {
            const errorMessage = error.response?.data?.message || error.message;
            if (errorMessage.includes("App is not live") || errorMessage.includes("Waba id not found")) {
              throw new Error(errorMessage);
            }
          }
        }
      }
    }

    if (needsAccountImage) {
      try {
        const profilePhotoUrl = await getGupshupProfilePhoto(appId);
        
        if (profilePhotoUrl) {
          try {
            const mediaSaveResponse = await apiT2B.post(
              mediaSaveFromUrl,
              {
                url: profilePhotoUrl,
              },
              {
                headers: {
                  Authorization: `Bearer ${userToken}`,
                  CustomerId: customerId,
                },
              }
            );
            publicUrl = mediaSaveResponse.data?.publicUrl;
          } catch (mediaError) {
            
          }
        }
      } catch (error) {
        
      }
    }

    // Construir objeto de atualização preservando TODOS os campos existentes
    // IMPORTANTE: Active deve ser sempre preservado do objeto completo do GET
    // Se o GET retornou Active, usar esse valor; senão, usar o valor original
    const activeValue =
      fullPlatform.Active !== undefined && fullPlatform.Active !== null
        ? fullPlatform.Active
        : whatsPlatform.Active !== undefined && whatsPlatform.Active !== null
          ? whatsPlatform.Active
          : true; // Fallback para true se não encontrar em nenhum lugar

    const updatedFields: ICustomerPlatformInput = {
      // Campos obrigatórios - sempre incluídos
      platformId: fullPlatform.platformId,
      customerExternalId: fullPlatform.customerExternalId || "",
      platformProfileId: fullPlatform.platformProfileId || "",
      Active: activeValue, // ✅ Preservar valor existente do GET (true ou false)

      // Campos opcionais - incluir apenas se existirem (não undefined/null)
      ...(fullPlatform.configuration !== undefined &&
        fullPlatform.configuration !== null && {
          configuration: fullPlatform.configuration,
        }),
      ...(fullPlatform.accessToken !== undefined &&
        fullPlatform.accessToken !== null && {
          accessToken: fullPlatform.accessToken,
        }),
      ...(fullPlatform.recoveryCode !== undefined &&
        fullPlatform.recoveryCode !== null && {
          recoveryCode: fullPlatform.recoveryCode,
        }),
      ...(fullPlatform.tokenExpiresAt !== undefined &&
        fullPlatform.tokenExpiresAt !== null && {
          tokenExpiresAt: fullPlatform.tokenExpiresAt,
        }),
      ...(fullPlatform.refreshToken !== undefined &&
        fullPlatform.refreshToken !== null && {
          refreshToken: fullPlatform.refreshToken,
        }),
      ...(fullPlatform.PlatformAccountInfo !== undefined &&
        fullPlatform.PlatformAccountInfo !== null && {
          PlatformAccountInfo: fullPlatform.PlatformAccountInfo,
        }),
      ...(fullPlatform.customerId !== undefined &&
        fullPlatform.customerId !== null && {
          customerId: fullPlatform.customerId,
        }),

      // Campos de exibição - usar valores novos se disponíveis, senão preservar existentes
      displayName: verified_name || fullPlatform.displayName || undefined,
      displayNumber: display_phone_number || fullPlatform.displayNumber || undefined,
      accountImage: publicUrl || fullPlatform.accountImage || undefined,
    };

    // Verificar se há mudanças nos campos essenciais antes de fazer PUT
    // Considera mudança se: novo valor existe E é diferente do atual OU campo atual está vazio e temos novo valor
    const hasDisplayNameChange =
      verified_name &&
      (verified_name !== fullPlatform.displayName || !fullPlatform.displayName);
    const hasDisplayNumberChange =
      display_phone_number &&
      (display_phone_number !== fullPlatform.displayNumber || !fullPlatform.displayNumber);
    const hasAccountImageChange =
      publicUrl && (publicUrl !== fullPlatform.accountImage || !fullPlatform.accountImage);

    // Só fazer PUT se houver mudanças nos campos essenciais
    if (hasDisplayNameChange || hasDisplayNumberChange || hasAccountImageChange) {
      // Remover campos undefined do objeto final (para não enviar no JSON)
      const cleanFields = Object.fromEntries(
        Object.entries(updatedFields).filter(([_, value]) => value !== undefined)
      ) as ICustomerPlatformInput;

      await apiT2B.put(`${baseCustomerPlatformUrl}/${whatsPlatform.id}`, cleanFields, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          CustomerId: customerId,
        },
      });
    }

    // Retornar objeto atualizado (com ou sem PUT)
    return {
      ...fullPlatform,
      displayName: updatedFields.displayName || fullPlatform.displayName,
      displayNumber: updatedFields.displayNumber || fullPlatform.displayNumber,
      accountImage: updatedFields.accountImage || fullPlatform.accountImage,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || "Failed to update GupShup general info");
    }

    throw new Error("Failed to update GupShup general info");
  }
}

